import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, MapPin, Truck } from 'lucide-react';
import { cities, services } from '../data/locationsSeo';
import Footer from './Footer';

const ServiceAreasPage = () => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = 'Service Areas | Pro-Wash Mobile Detailing — Fraser Valley, BC';

    let metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute('content') : null;
    if (!metaDesc) {
      metaDesc = document.createElement('meta');
      metaDesc.setAttribute('name', 'description');
      document.head.appendChild(metaDesc);
    }
    metaDesc.setAttribute(
      'content',
      'Pro-Wash Mobile Detailing service areas across the Fraser Valley: Abbotsford, Mission, Langley, Maple Ridge & Chilliwack. Explore all detailing and ceramic coating services by city.'
    );

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute('content', prevDesc);
    };
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0F172A' }}>
      {/* Header */}
      <section
        data-testid="service-areas-header"
        className="relative overflow-hidden border-b border-slate-800"
      >
        <div
          className="absolute inset-0 pointer-events-none opacity-40"
          style={{
            background:
              'radial-gradient(circle at 20% 0%, rgba(37, 99, 235, 0.25) 0%, transparent 45%), radial-gradient(circle at 80% 100%, rgba(37, 99, 235, 0.15) 0%, transparent 50%)',
          }}
        />

        <div className="container relative z-10 px-4 md:px-8 pt-24 pb-12 md:pt-32 md:pb-16">
          {/* Breadcrumbs */}
          <nav
            data-testid="service-areas-breadcrumbs"
            className="flex items-center flex-wrap gap-x-2 gap-y-1 text-xs md:text-sm text-slate-400 mb-8"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <ChevronRight size={14} className="text-slate-600" />
            <span className="text-blue-400">Service Areas</span>
          </nav>

          <div className="inline-flex items-center gap-2 mb-5 px-4 py-1.5 bg-blue-500/10 border border-blue-500/30 rounded-full">
            <Truck size={14} className="text-blue-400" />
            <span className="text-blue-100 text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em]">
              100% Mobile Service
            </span>
          </div>

          <h1
            data-testid="service-areas-h1"
            className="brand-display text-white leading-[1.05] tracking-tight text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-5"
          >
            Service Areas
          </h1>

          <p className="text-slate-300 max-w-3xl text-base md:text-xl font-light leading-relaxed">
            Premium mobile detailing delivered to your home or workplace across the Fraser Valley.
            Choose your city below to see every service we offer there.
          </p>
        </div>
      </section>

      {/* Cities Grid */}
      <section className="container px-4 md:px-8 py-16 md:py-24">
        <div
          data-testid="service-areas-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8"
        >
          {cities.map((city) => (
            <div
              key={city.slug}
              data-testid={`city-card-${city.slug}`}
              className="group relative bg-slate-900/60 backdrop-blur-sm border border-slate-800 rounded-2xl p-6 md:p-7 hover:border-blue-500/40 transition-colors"
            >
              {/* City header */}
              <div className="flex items-start justify-between mb-5">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <MapPin size={16} className="text-blue-400" />
                    <span className="text-[10px] uppercase tracking-widest text-blue-400 font-semibold">
                      {city.province}, Canada
                    </span>
                  </div>
                  <h2 className="text-2xl md:text-3xl font-bold text-white">
                    {city.name}
                  </h2>
                </div>
              </div>

              {/* Services list */}
              <ul className="space-y-2">
                {services.map((service) => (
                  <li key={service.slug}>
                    <Link
                      to={`/locations/${city.slug}/${service.slug}`}
                      data-testid={`link-${city.slug}-${service.slug}`}
                      className="flex items-center justify-between gap-3 px-4 py-3 rounded-lg bg-slate-950/60 border border-slate-800 hover:border-blue-500/40 hover:bg-slate-950/90 transition-all group/link"
                    >
                      <span className="text-sm text-slate-200 group-hover/link:text-white font-medium leading-snug">
                        {service.heroTitle || service.name}
                      </span>
                      <ChevronRight
                        size={16}
                        className="text-slate-600 group-hover/link:text-blue-400 flex-shrink-0 transition-colors"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Not-in-your-city fallback */}
        <div
          data-testid="service-areas-fallback"
          className="mt-12 md:mt-16 bg-gradient-to-br from-blue-500/10 to-transparent border border-blue-500/20 rounded-2xl p-6 md:p-8 text-center max-w-3xl mx-auto"
        >
          <h3 className="text-xl md:text-2xl font-bold text-white mb-2">
            Don&apos;t see your city?
          </h3>
          <p className="text-slate-300 text-sm md:text-base mb-5">
            We regularly serve surrounding areas across the Fraser Valley. Reach out and we&apos;ll confirm coverage.
          </p>
          <Link
            to="/book-now"
            data-testid="fallback-book-cta"
            className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm md:text-base"
          >
            Book Your Detail
            <ChevronRight size={18} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServiceAreasPage;
